import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { axiosGetV3, axiosPostV3 } from '../../api/axios-handler';
import useAuth from '../utils/useAuth';
import { THEME_ID, type ThemeId } from '../../themes/types';

export enum VersionedRecordKey {
  MEMBER_TERMS_OF_SERVICE = 'member_terms_of_service',
  MEMBER_PRIVACY_POLICY = 'member_privacy_policy',
  KINDLY_CHRISTIAN_MEMBER_TERMS_OF_SERVICE = 'kindly_christian_member_terms_of_service',
  KINDLY_CHRISTIAN_MEMBER_PRIVACY_POLICY = 'kindly_christian_member_privacy_policy',
}

export function getMemberTermsKey(themeId: ThemeId): VersionedRecordKey {
  return themeId === THEME_ID.KINDLY_CHRISTIAN
    ? VersionedRecordKey.KINDLY_CHRISTIAN_MEMBER_TERMS_OF_SERVICE
    : VersionedRecordKey.MEMBER_TERMS_OF_SERVICE;
}

export function getMemberPrivacyKey(themeId: ThemeId): VersionedRecordKey {
  return themeId === THEME_ID.KINDLY_CHRISTIAN
    ? VersionedRecordKey.KINDLY_CHRISTIAN_MEMBER_PRIVACY_POLICY
    : VersionedRecordKey.MEMBER_PRIVACY_POLICY;
}

export interface VersionedRecordResponse {
  created_at: string;
  id: number;
  key: string;
  text: string;
  version: number;
}

const getVersionedRecord = async (key: VersionedRecordKey): Promise<VersionedRecordResponse> => {
  const response = await axiosGetV3(`/versioned_records/${key}?latest=true`, {});
  return response.data;
};

export const useVersionedRecord = (key: VersionedRecordKey) => {
  const { data, isLoading, error, refetch, isFetching } = useQuery<VersionedRecordResponse>(
    ['versioned-record', key],
    () => getVersionedRecord(key),
    {
      refetchOnWindowFocus: false,
    },
  );

  return {
    data,
    isLoading,
    error,
    refetch,
    isFetching,
  };
};

export interface VersionedRecordAcceptanceResponse {
  accepted: boolean;
}

const getVersionedRecordAcceptance = async (key: VersionedRecordKey): Promise<VersionedRecordAcceptanceResponse> => {
  const response = await axiosGetV3(`/versioned_records/${key}/current`, {});
  return response.data;
};

export const useVersionedRecordAcceptance = (key: VersionedRecordKey) => {
  const { user } = useAuth();

  const { data, isLoading, error, refetch, isFetching } = useQuery<VersionedRecordAcceptanceResponse>(
    ['versioned-record-acceptance', key],
    () => getVersionedRecordAcceptance(key),
    {
      enabled: Boolean(user),
    },
  );

  return {
    data,
    isLoading,
    error,
    refetch,
    isFetching,
  };
};

const acceptVersionedRecordRequest = async (key: VersionedRecordKey, versionId?: number) => {
  const payload = {
    record_key: key,
    version_id: versionId,
  };

  return await axiosPostV3(`/versioned_records/accept`, payload);
};

export const useAcceptVersionedRecord = () => {
  const queryClient = useQueryClient();
  const acceptVersionedRecord = useMutation(
    ({ versionId, key }: { versionId?: number; key: VersionedRecordKey }) => {
      return acceptVersionedRecordRequest(key, versionId);
    },
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(['versioned-record-acceptance', variables.key]);
      },
    },
  );
  return { acceptVersionedRecord };
};
